import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Bell, BellOff, BellRing, ChevronDown, Loader2, Smartphone } from "lucide-react";
import { Button } from "../ui/button";
import useWebPush from "../../hooks/useWebPush";
import MyDevicesPanel from "../push/MyDevicesPanel";

/**
 * RC-WEB-4 §17/§18/§19/§20/§39 — ONE notification-preference model.
 *
 * Primary concept  : AvisoCar app notifications (store links come from the
 *                    shared CMS, so launch day needs no code change).
 * Fallback, demoted: browser notifications, inside an advanced section.
 * Removed          : "push gratis", "0 costo", "instala AvisoCar en tu pantalla
 *                    de inicio" as primary messaging, and the old
 *                    "Recordatorio de emergencia (60s)" wording.
 */

import { useI18n } from "../../i18n";

const API = "/api";

const Toggle = ({ id, label, description, checked, onChange }) => (
  <label className="flex cursor-pointer items-start justify-between gap-4 rounded-xl border border-slate-200 p-4">
    <span className="min-w-0">
      <span className="block text-sm font-semibold text-[#0B2545]">{label}</span>
      <span className="block text-xs text-slate-600">{description}</span>
    </span>
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-1 h-5 w-5 shrink-0 accent-[#1B84FF]"
      data-testid={id}
    />
  </label>
);

export const NotificationsSection = () => {
  const { t } = useI18n();
  const push = useWebPush();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [appLinks, setAppLinks] = useState({});
  const [advanced, setAdvanced] = useState(false);

  useEffect(() => {
    axios.get(`${API}/user/notification-settings`)
      .then((r) => setSettings(r.data))
      .catch(() => toast.error(t("app.notif.load_error")));
    axios.get(`${API}/shared/content`)
      .then((r) => setAppLinks(r.data?.app_links || {}))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (patch) => {
    setSaving(true);
    const next = { ...settings, ...patch };
    setSettings(next);
    try {
      const res = await axios.put(`${API}/user/notification-settings`, {
        messages_enabled: next.messages_enabled,
        calls_enabled: next.calls_enabled,
        urgent_reminders_enabled: next.urgent_reminders_enabled,
      });
      setSettings(res.data);
    } catch (_e) {
      toast.error(t("app.notif.save_error"));
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#1B84FF]" /></div>;
  }

  const androidUrl = (appLinks.android_store_url || "").trim();
  const iosUrl = (appLinks.ios_store_url || "").trim();
  const hasStore = Boolean(androidUrl || iosUrl);

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="notifications-card">
        <h2 className="mb-1 text-lg font-semibold text-[#0B2545]">{t("app.notif.title")}</h2>
        <p className="mb-5 text-sm text-slate-600">
          {t("app.notif.desc")}
        </p>
        <div className="space-y-3">
          <Toggle
            id="notif-messages-toggle"
            label={t("app.notif.msg_label")}
            description={t("app.notif.msg_desc")}
            checked={settings.messages_enabled}
            onChange={(v) => save({ messages_enabled: v })}
          />
          <Toggle
            id="notif-calls-toggle"
            label={t("app.notif.call_label")}
            description={t("app.notif.call_desc")}
            checked={settings.calls_enabled}
            onChange={(v) => save({ calls_enabled: v })}
          />
          <Toggle
            id="notif-urgent-reminder-toggle"
            label={t("app.notif.repeat_label")}
            description={t("app.notif.repeat_desc")}
            checked={settings.urgent_reminders_enabled}
            onChange={(v) => save({ urgent_reminders_enabled: v })}
          />
        </div>
        {saving && <p className="mt-3 text-xs text-slate-600">{t("app.notif.saving")}</p>}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="app-notifications-card">
        <div className="mb-3 flex items-start gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#1B84FF]/15 text-[#1B84FF]">
            <Smartphone className="h-5 w-5" />
          </span>
          <div>
            <h2 className="text-lg font-semibold text-[#0B2545]">{t("app.notif.app_title")}</h2>
            <p className="text-sm text-slate-600">
              {t("app.notif.app_desc")}
            </p>
          </div>
        </div>
        {hasStore ? (
          <div className="flex flex-wrap gap-2" data-testid="app-store-links">
            {androidUrl && (
              <a href={androidUrl} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#0B2545] text-white hover:bg-[#123a6b]" data-testid="android-store-btn">
                  {t("app.help.android_download")}
                </Button>
              </a>
            )}
            {iosUrl && (
              <a href={iosUrl} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#0B2545] text-white hover:bg-[#123a6b]" data-testid="ios-store-btn">
                  {t("app.help.ios_download")}
                </Button>
              </a>
            )}
          </div>
        ) : (
          <p className="rounded-xl bg-slate-50 p-3 text-sm text-slate-700" data-testid="app-not-available-note">
            {t("app.notif.app_pending")}
          </p>
        )}
        {settings.app_devices_registered > 0 && (
          <p className="mt-3 text-xs text-slate-600" data-testid="app-devices-count">
            {t("app.notif.devices")}: {settings.app_devices_registered}
          </p>
        )}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white" data-testid="browser-push-section">
        <button
          onClick={() => setAdvanced((v) => !v)}
          className="flex w-full items-center justify-between gap-3 p-5 text-left"
          data-testid="browser-push-toggle-section"
        >
          <span>
            <span className="block text-sm font-semibold text-[#0B2545]">
              {t("app.notif.browser_title")}
            </span>
            <span className="block text-xs text-slate-600">
              {t("app.notif.browser_alt")} {settings.browser_push_enabled ? t("app.notif.on") : t("app.notif.off")}.
            </span>
          </span>
          <ChevronDown className={`h-5 w-5 shrink-0 text-slate-600 transition-transform ${advanced ? "rotate-180" : ""}`} />
        </button>

        {advanced && (
          <div className="space-y-4 border-t border-slate-200 p-5" data-testid="browser-push-advanced">
            <div className="flex flex-wrap gap-2">
              {!push.subscribed ? (
                <Button
                  type="button"
                  disabled={push.busy || !push.supported || push.permission === "denied"}
                  onClick={async () => {
                    const ok = await push.enablePush();
                    if (ok) toast.success(t("app.notif.enabled_toast"));
                    else if (push.lastError) toast.error(push.lastError);
                  }}
                  className="bg-[#1B84FF] text-white hover:bg-[#0F6FDB]"
                  data-testid="push-enable-btn"
                >
                  <BellRing className="mr-2 h-4 w-4" /> {t("app.notif.enable_btn")}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  disabled={push.busy}
                  onClick={async () => { await push.disablePush(); toast.info(t("app.notif.disabled_toast")); }}
                  className="border-slate-300 text-[#0B2545]"
                  data-testid="push-disable-btn"
                >
                  <BellOff className="mr-2 h-4 w-4" /> {t("app.notif.disable_btn")}
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                disabled={!push.subscribed || push.busy}
                onClick={async () => {
                  const r = await push.sendTest();
                  if (r?.ok) toast.success(`${t("app.notif.test_ok")} (${r.sent}/${r.attempted}).`);
                  else toast.error(t("app.notif.test_fail"));
                }}
                className="border-slate-300 text-[#0B2545]"
                data-testid="push-test-btn"
              >
                <Bell className="mr-2 h-4 w-4" /> {t("app.notif.test_btn")}
              </Button>
            </div>

            {!push.supported && (
              <p className="text-xs text-amber-800" data-testid="push-unsupported-note">
                {t("app.notif.unsupported")} <Link to="/ayuda/usar-en-navegador" className="font-semibold underline">{t("app.notif.unsupported_link")}</Link>.
              </p>
            )}
            {push.lastError && <p className="text-xs text-red-700" data-testid="push-error">{push.lastError}</p>}

            <p className="text-xs text-slate-600" data-testid="push-best-effort-note">
              {t("app.notif.best_effort")}
            </p>

            <MyDevicesPanel
              currentEndpointHash={push.currentEndpointHash}
              busy={push.busy}
              onReactivate={async () => { await push.enablePush(); }}
            />
          </div>
        )}
      </section>
    </div>
  );
};

export default NotificationsSection;
